import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getSponsorDashboard, getSponsorContacts } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  ListTodo, FileText, Calendar, MessageSquare, Megaphone,
  Clock, CheckCircle2, Circle, Users, Mail, Phone
} from 'lucide-react';

const TASK_STATUS = {
  pendiente: { label: 'Pendiente', icon: Circle, color: 'bg-stone-100 text-stone-700' },
  en_progreso: { label: 'En Progreso', icon: Clock, color: 'bg-blue-100 text-blue-700' },
  completado: { label: 'Completado', icon: CheckCircle2, color: 'bg-emerald-100 text-emerald-700' },
};

export default function SponsorPortal() {
  const { hasPermission, user } = useAuth();
  const [data, setData] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [dashRes, contactRes] = await Promise.all([
        getSponsorDashboard(),
        getSponsorContacts()
      ]);
      setData(dashRes.data);
      setContacts(contactRes.data);
    } catch (err) {
      console.error(err);
      toast.error('Error cargando datos del portal');
    } finally {
      setLoading(false);
    }
  };

  if (!hasPermission('patrocinadores', 'ver')) {
    return <div className="text-center py-20 text-stone-500" data-testid="access-denied">Acceso denegado</div>;
  }

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  const stats = data?.task_stats || { pendiente: 0, en_progreso: 0, completado: 0 };

  return (
    <div className="space-y-6" data-testid="sponsor-portal-page">
      <div>
        <h1 className="text-2xl font-bold text-stone-800">Portal de Patrocinadores</h1>
        <p className="text-stone-500 text-sm mt-1">
          Bienvenido, {user?.full_name} — {data?.sponsor_info?.tipo_patrocinador || 'Colaborador'}
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card data-testid="stat-pendiente">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-stone-100"><Circle className="w-5 h-5 text-stone-600" /></div>
            <div>
              <p className="text-2xl font-bold text-stone-800">{stats.pendiente}</p>
              <p className="text-xs text-stone-500">Pendientes</p>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="stat-en-progreso">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-100"><Clock className="w-5 h-5 text-blue-600" /></div>
            <div>
              <p className="text-2xl font-bold text-stone-800">{stats.en_progreso}</p>
              <p className="text-xs text-stone-500">En Progreso</p>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="stat-completado">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-100"><CheckCircle2 className="w-5 h-5 text-emerald-600" /></div>
            <div>
              <p className="text-2xl font-bold text-stone-800">{stats.completado}</p>
              <p className="text-xs text-stone-500">Completadas</p>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="stat-mensajes">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-100"><MessageSquare className="w-5 h-5 text-amber-600" /></div>
            <div>
              <p className="text-2xl font-bold text-stone-800">{data?.unread_messages || 0}</p>
              <p className="text-xs text-stone-500">Mensajes sin leer</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="sponsor-tabs">
          <TabsTrigger value="overview" data-testid="tab-overview">
            <ListTodo className="w-4 h-4 mr-1.5" /> Tareas
          </TabsTrigger>
          <TabsTrigger value="events" data-testid="tab-events">
            <Calendar className="w-4 h-4 mr-1.5" /> Eventos
          </TabsTrigger>
          <TabsTrigger value="docs" data-testid="tab-docs">
            <FileText className="w-4 h-4 mr-1.5" /> Documentos
          </TabsTrigger>
          <TabsTrigger value="contacts" data-testid="tab-contacts">
            <Users className="w-4 h-4 mr-1.5" /> Contactos
          </TabsTrigger>
          <TabsTrigger value="announcements" data-testid="tab-announcements">
            <Megaphone className="w-4 h-4 mr-1.5" /> Anuncios
          </TabsTrigger>
        </TabsList>

        {/* Tasks Tab */}
        <TabsContent value="overview">
          <Card>
            <CardHeader><CardTitle className="text-lg">Mis Tareas Asignadas</CardTitle></CardHeader>
            <CardContent>
              {data?.tasks?.length === 0 ? (
                <p className="text-stone-400 text-center py-8">No tienes tareas asignadas</p>
              ) : (
                <div className="space-y-3">
                  {data?.tasks?.map(task => {
                    const status = TASK_STATUS[task.estado] || TASK_STATUS.pendiente;
                    const Icon = status.icon;
                    return (
                      <div key={task.id} className="flex items-center gap-3 p-3 rounded-lg border hover:bg-stone-50 transition-colors" data-testid={`sponsor-task-${task.id}`}>
                        <Icon className="w-5 h-5 text-stone-400 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-stone-800 text-sm">{task.titulo}</p>
                          {task.descripcion && <p className="text-xs text-stone-500 truncate">{task.descripcion}</p>}
                        </div>
                        <Badge className={`text-xs shrink-0 ${status.color}`}>{status.label}</Badge>
                        {task.fecha_limite && (
                          <span className="text-xs text-stone-400 shrink-0">{task.fecha_limite?.slice(0, 10)}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Events Tab */}
        <TabsContent value="events">
          <Card>
            <CardHeader><CardTitle className="text-lg">Eventos Próximos</CardTitle></CardHeader>
            <CardContent>
              {data?.events?.length === 0 ? (
                <p className="text-stone-400 text-center py-8">No hay eventos próximos</p>
              ) : (
                <div className="space-y-3">
                  {data?.events?.map(event => (
                    <div key={event.id} className="flex items-center gap-3 p-3 rounded-lg border hover:bg-stone-50" data-testid={`sponsor-event-${event.id}`}>
                      <Calendar className="w-5 h-5 text-emerald-500 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-stone-800 text-sm">{event.title || event.titulo}</p>
                        <p className="text-xs text-stone-500">{event.description || event.descripcion || ''}</p>
                      </div>
                      <span className="text-xs text-stone-400 shrink-0">{event.start_date?.slice(0, 10)}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="docs">
          <Card>
            <CardHeader><CardTitle className="text-lg">Documentos Compartidos</CardTitle></CardHeader>
            <CardContent>
              {data?.documents?.length === 0 ? (
                <p className="text-stone-400 text-center py-8">No hay documentos disponibles</p>
              ) : (
                <div className="space-y-3">
                  {data?.documents?.map(doc => (
                    <div key={doc.id} className="flex items-center gap-3 p-3 rounded-lg border hover:bg-stone-50" data-testid={`sponsor-doc-${doc.id}`}>
                      <FileText className="w-5 h-5 text-blue-500 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-stone-800 text-sm">{doc.name || doc.nombre}</p>
                        <p className="text-xs text-stone-500">{doc.category || doc.categoria || 'General'}</p>
                      </div>
                      <span className="text-xs text-stone-400 shrink-0">{doc.created_at?.slice(0, 10)}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Contacts Tab */}
        <TabsContent value="contacts">
          <Card>
            <CardHeader><CardTitle className="text-lg">Directiva - Contactos</CardTitle></CardHeader>
            <CardContent>
              {contacts.length === 0 ? (
                <p className="text-stone-400 text-center py-8">No hay contactos disponibles</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {contacts.map(c => (
                    <div key={c.id} className="flex items-center gap-3 p-4 rounded-lg border hover:bg-stone-50" data-testid={`sponsor-contact-${c.id}`}>
                      <Avatar className="w-10 h-10">
                        <AvatarImage src={c.avatar_url} />
                        <AvatarFallback className="bg-emerald-100 text-emerald-700">{c.full_name?.[0]}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-stone-800 text-sm">{c.full_name}</p>
                        <p className="text-xs text-stone-500">{c.role_name}</p>
                        <p className="text-xs text-stone-400">{c.email}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Announcements Tab */}
        <TabsContent value="announcements">
          <Card>
            <CardHeader><CardTitle className="text-lg">Anuncios Recientes</CardTitle></CardHeader>
            <CardContent>
              {data?.announcements?.length === 0 ? (
                <p className="text-stone-400 text-center py-8">No hay anuncios</p>
              ) : (
                <div className="space-y-3">
                  {data?.announcements?.map(ann => (
                    <div key={ann.id} className="p-4 rounded-lg border hover:bg-stone-50" data-testid={`sponsor-ann-${ann.id}`}>
                      <div className="flex items-start gap-3">
                        <Megaphone className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium text-stone-800 text-sm">{ann.title}</p>
                          <p className="text-xs text-stone-500 mt-1">{ann.content}</p>
                          <p className="text-xs text-stone-400 mt-2">{ann.created_at?.slice(0, 10)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
